"use client";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Inventory as InventoryIcon,
  CheckCircleOutline as ResolveIcon,
} from "@mui/icons-material";
import moment from "moment/moment";

// ─── Estado helpers ────────────────────────────────────────────────────────────

function EstadoChip({ estado, vencimiento }) {
  const props = {
    vencida: { label: "Vencida", color: "#b91c1c", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.25)" },
    por_vencer: {
      label: `Vence en ${vencimiento ? moment(vencimiento).diff(moment(), "days") : 0}d`,
      color: "#b45309",
      bg: "rgba(245,158,11,0.1)",
      border: "rgba(245,158,11,0.3)",
    },
    al_dia: { label: "Al día", color: "#15803d", bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.3)" },
    inactiva: { label: "Inactiva", color: "#64748b", bg: "rgba(100,116,139,0.1)", border: "#e2e8f0" },
  }[estado] ?? { label: "—", color: "#64748b", bg: "transparent", border: "#e2e8f0" };

  return (
    <Chip
      size="small"
      label={props.label}
      variant="outlined"
      sx={{
        fontSize: 11,
        height: 22,
        color: props.color,
        bgcolor: props.bg,
        borderColor: props.border,
        fontWeight: 600,
      }}
    />
  );
}

// ─── TablaTareas ───────────────────────────────────────────────────────────────

export function TablaTareas({ tareas, obtenerEstadoTarea, onEditar, onMarcarInactiva, onEliminar, onResolverAlerta }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f8fafc" }}>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569", width: "40%" }}>
              Tarea
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }}>
              Vencimiento / Estado
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }} align="center">
              Req.
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }} align="right">
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tareas.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary", fontSize: 13 }}>
                No hay tareas para mostrar.
              </TableCell>
            </TableRow>
          )}
          {tareas.map((tarea) => {
            const estado = obtenerEstadoTarea(tarea);
            const isInactive = estado === "inactiva";
            const materialAlert =
              tarea.alerta_material &&
              moment(tarea.alerta_material).diff(moment(), "days") <= 7;

            return (
              <TableRow
                key={tarea.id}
                hover
                sx={{
                  opacity: isInactive ? 0.55 : 1,
                  "&:last-child td": { border: 0 },
                }}
              >
                {/* ── Tarea ── */}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25, flexWrap: "wrap" }}>
                        {tarea.periodo && (
                          <Chip
                            label={tarea.periodo}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: "rgba(59,130,246,0.1)",
                              color: "#1d4ed8",
                              border: "1px solid rgba(59,130,246,0.2)",
                            }}
                            variant="outlined"
                          />
                        )}
                        {materialAlert && (
                          <Tooltip title={`Material vence el ${moment(tarea.alerta_material).format("DD/MM/YYYY")}`}>
                            <Chip
                              icon={<InventoryIcon sx={{ fontSize: "11px !important" }} />}
                              label="Mat. por vencer"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                bgcolor: "rgba(245,158,11,0.1)",
                                color: "#b45309",
                                border: "1px solid rgba(245,158,11,0.3)",
                              }}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Tooltip title={tarea.tarea} placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: 13,
                            lineHeight: 1.45,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: "#0f172a",
                          }}
                        >
                          {tarea.tarea}
                        </Typography>
                      </Tooltip>
                      {tarea.responsable && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {tarea.responsable}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                {/* ── Vencimiento / Estado ── */}
                <TableCell>
                  <EstadoChip estado={estado} vencimiento={tarea.vencimiento} />
                  {tarea.vencimiento && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {moment(tarea.vencimiento).format("DD/MM/YYYY")}
                    </Typography>
                  )}
                  {tarea.ultimo && (
                    <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontSize: 11 }}>
                      Últ.: {moment(tarea.ultimo).format("DD/MM/YYYY")}
                    </Typography>
                  )}
                </TableCell>

                {/* ── Requisitos ── */}
                <TableCell align="center">
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                    {tarea.instructivohsse ? (
                      <Chip label="HSSE" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "rgba(239,68,68,0.08)", color: "#b91c1c", border: "1px solid rgba(239,68,68,0.2)" }} variant="outlined" />
                    ) : null}
                    {tarea.cert_especial ? (
                      <Chip label="Cert." size="small" sx={{ height: 18, fontSize: 10, bgcolor: "rgba(168,85,247,0.08)", color: "#7c3aed", border: "1px solid rgba(168,85,247,0.2)" }} variant="outlined" />
                    ) : null}
                    {!tarea.instructivohsse && !tarea.cert_especial && (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* ── Acciones ── */}
                <TableCell align="right">
                  <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                    <Tooltip title="Editar">
                      <span>
                        <IconButton size="small" onClick={() => onEditar(tarea)} disabled={isInactive} sx={{ color: "#475569", "&:hover": { color: "#1d4ed8" } }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={Number(tarea.activa) === 1 ? "Desactivar" : "Activar"}>
                      <IconButton size="small" onClick={() => onMarcarInactiva(tarea)} sx={{ color: Number(tarea.activa) === 1 ? "#22c55e" : "#94a3b8", "&:hover": { color: Number(tarea.activa) === 1 ? "#15803d" : "#22c55e" } }}>
                        {Number(tarea.activa) === 1 ? (
                          <ToggleOnIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <ToggleOffIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    {tarea.alerta_material && (
                      <Tooltip title={`Marcar alerta de materiales como resuelta (${moment(tarea.alerta_material).format("DD/MM/YYYY")})`}>
                        <IconButton
                          size="small"
                          onClick={() => onResolverAlerta?.(tarea)}
                          sx={{ color: "#f59e0b", "&:hover": { color: "#15803d" } }}
                        >
                          <ResolveIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => onEliminar(tarea)} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
